import { SelectOptionType } from "../types";
import {
  BODY_PART_OPTIONS,
  SEDGWICK_NATURE_OF_INJURY_OPTIONS,
  SEDGWICK_CAUSE_OF_INJURY_OPTIONS,
} from "../options";

/**
 * Canada-specific concern options (completely replaces US list).
 */
export const SEDGWICK_CANADA_CONCERNS_OPTIONS: SelectOptionType[] = [
  { value: "Late Report", label: "Late Report" },
  {
    value: "Less than 90 Days of Employment",
    label: "Less than 90 Days of Employment",
  },
  {
    value: "No/Inconsistent MOI",
    label: "No/Inconsistent MOI",
  },
  {
    value: "No/Unknown Specific Onset Date/Time",
    label: "No/Unknown Specific Onset Date/Time",
  },
  { value: "Other", label: "Other" },
  {
    value: "Pre-Existing Condition Impacting Affected Part",
    label: "Pre-Existing Condition Impacting Affected Part",
  },
  {
    value: "Prior Injury to Same Body Part",
    label: "Prior Injury to Same Body Part",
  },
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Additional Canada-specific body part entries not in the US list.
 */
const SEDGWICK_CANADA_ADDITIONAL_BODY_PARTS: SelectOptionType[] = [
  { value: "9100", label: "Body Systems & Multiple Body Systems" },
  { value: "2102", label: "Cervical" },
  { value: "9102", label: "Circulatory System" },
  { value: "9103", label: "Digestive System" },
  { value: "4300", label: "Disc (Back)" },
  { value: "2200", label: "Disc (Neck)" },
  { value: "4900", label: "Heart" },
  {
    value: "6500",
    label: "Insufficient Info to Identify; Unclassified",
  },
  { value: "4800", label: "Internal Organs" },
  { value: "2400", label: "Larynx" },
  { value: "6300", label: "Lumbar and/or Sacral Vertebrae" },
  { value: "6000", label: "Lung(s)" },
  { value: "1000", label: "Multiple Head Injury" },
  { value: "2000", label: "Multiple Neck Injury" },
  { value: "9104", label: "Muscular-Skeletal System" },
  { value: "9105", label: "Nervous System" },
  { value: "9106", label: "Respiratory System" },
  { value: "2300", label: "Spinal Cord" },
  { value: "4700", label: "Spinal Cord (Trunk)" },
  { value: "2600", label: "Trachea" },
  { value: "2100", label: "Vertebrae" },
  { value: "4801", label: "Uro-Genital" },
];

/**
 * Canada body part options: US body parts + 22 additional entries.
 * Code 1001 label changed from "Head" to "Head NEC".
 */
export const SEDGWICK_CANADA_BODY_PART_OPTIONS: SelectOptionType[] = [
  // US body parts with code 1001 label override
  ...BODY_PART_OPTIONS.filter((opt) => opt.value !== "1001"),
  { value: "1001", label: "Head NEC" },
  // Canada-specific additions
  ...SEDGWICK_CANADA_ADDITIONAL_BODY_PARTS,
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Additional Canada-specific nature of injury entries not in the US list.
 */
const SEDGWICK_CANADA_ADDITIONAL_NATURE_OF_INJURY: SelectOptionType[] = [
  {
    value: "3800",
    label: "Adverse Reaction to Vaccination or Inoculation",
  },
  { value: "7500", label: "AIDS" },
  { value: "5900", label: "All Other (Specific) Injuries, NOC" },
  { value: "8000", label: "All Other Cumulative Injury" },
  { value: "7100", label: "All Other Occupational Disease" },
  { value: "0300", label: "Angina Pectoris" },
  { value: "3704", label: "Arthritis" },
  { value: "6100", label: "Asbestosis" },
  { value: "6200", label: "Black Lung" },
  { value: "3702", label: "Bursitis" },
  { value: "6300", label: "Byssinosis" },
  { value: "7400", label: "Cancer" },
  { value: "7800", label: "Carpal Tunnel Syndrome" },
  { value: "0700", label: "Concussion" },
  { value: "7300", label: "Contagious Disease" },
  { value: "5801", label: "Damaged Eyeglasses" },
  { value: "8001", label: "DeQuervain's Syndrome" },
  { value: "6000", label: "Dust Disease NOC" },
  { value: "2200", label: "Enucleation" },
  { value: "4201", label: "Exposure to Anthrax Spores" },
  { value: "7801", label: "Ganglion" },
  { value: "3200", label: "Heat Prostration" },
  { value: "3400", label: "Hernia (Rupture)" },
  { value: "1601", label: "Herniated Disc" },
  { value: "4901", label: "Meniscus Tear" },
  { value: "6900", label: "Mental Disorder" },
  {
    value: "9100",
    label: "Multiple Injury Inc. Physical & Psychological",
  },
  { value: "9000", label: "Multiple Physical Injury Only" },
  { value: "4100", label: "Myocardial Infarction (Heart Attack)" },
  { value: "6600", label: "Poisoning - Chemical" },
  { value: "6700", label: "Poisoning - Metal" },
  { value: "6701", label: "Poisoning-Non Chemical" },
  { value: "6601", label: "Poisoning, Systemic" },
  { value: "7000", label: "Radiation" },
  { value: "5901", label: "Reflex Sympathetic Dystrophy (RSD)" },
  { value: "6500", label: "Respiratory Disorders" },
  { value: "4902", label: "Rotator Cuff Tear" },
  { value: "4600", label: "Rupture" },
  { value: "4700", label: "Severance" },
  { value: "6400", label: "Silicosis" },
  { value: "3703", label: "Tenosynovitis" },
  { value: "7301", label: "Tuberculosis" },
  { value: "5906", label: "Ulcerations" },
  { value: "5999", label: "Unclassified, Insufficient Data" },
  { value: "5907", label: "Varicosities" },
  { value: "5500", label: "Vascular Loss" },
  { value: "7600", label: "VDT-Related Disease" },
];

/**
 * Canada nature of injury options: US options + ~47 additional entries (97 total).
 */
export const SEDGWICK_CANADA_NATURE_OF_INJURY_OPTIONS: SelectOptionType[] = [
  ...SEDGWICK_NATURE_OF_INJURY_OPTIONS,
  ...SEDGWICK_CANADA_ADDITIONAL_NATURE_OF_INJURY,
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Additional Canada-specific cause of injury entries not in the US list.
 */
const SEDGWICK_CANADA_ADDITIONAL_CAUSE_OF_INJURY: SelectOptionType[] = [
  { value: "9901", label: "Air Pressure" },
  { value: "0302", label: "Atmospheric Environment" },
  { value: "6001", label: "Bodily Reaction" },
  {
    value: "1002",
    label: "Caught In/Between-Boiler, Pressure Vessel",
  },
  {
    value: "1003",
    label: "Caught In/Between-Mechanical Power Transmission",
  },
  { value: "2000", label: "Caught In/Between-Collapsing Material" },
  { value: "0900", label: "Contact With Not Otherwise Classified" },
  { value: "4700", label: "Crash of Airplane" },
  { value: "4100", label: "Crash of Rail Vehicle" },
  { value: "4000", label: "Crash of Water Vehicle" },
  { value: "2800", label: "Fall/Slip into Openings" },
  { value: "9300", label: "Gunshot" },
  { value: "9807", label: "Heart Attack/Condition" },
  { value: "9904", label: "Infectious Agents" },
  { value: "9100", label: "Mold, including mildew" },
  { value: "1700", label: "Object Being Lifted or Handled" },
  { value: "8800", label: "Natural Disasters" },
  { value: "9808", label: "Occupational Disease NEC" },
  { value: "9000", label: "Other than Physical Cause of Injury" },
  { value: "8300", label: "Pandemic" },
  { value: "5001", label: "Public Transportation Accident" },
  { value: "9905", label: "Radiating Substance" },
  { value: "6005", label: "Recreational Activity" },
  { value: "9400", label: "Repetitive Motion (separate entry)" },
  { value: "9804", label: "Repetitive Motion (before 7/1/94)" },
  { value: "6004", label: "Running and Training" },
  { value: "9500", label: "Rubbed or Abraded, NOC" },
  {
    value: "6700",
    label: "Sanding, Scraping, Cleaning Operation",
  },
  { value: "9802", label: "Silica" },
  { value: "9906", label: "Soap" },
  { value: "5903", label: "Strain By Using Casting" },
  { value: "5902", label: "Strain By Using Pump or Prime Mover" },
  { value: "6000", label: "Strain or Injury By, NOC" },
  { value: "7901", label: "Struck/Injured By Ceramic Item" },
  {
    value: "7902",
    label: "Struck/Injured By Coal or Petroleum Product",
  },
  { value: "7903", label: "Struck/Injured By Metal Item NOC" },
  {
    value: "8102",
    label: "Struck/Injured By Mineral Items, Metallic",
  },
  {
    value: "8103",
    label: "Struck/Injured By Mineral Items, Non-Metallic",
  },
  { value: "7410", label: "Struck/Injured By Patient" },
  { value: "7910", label: "Struck/Injured By Textile Item NEC" },
  { value: "7911", label: "Struck/Injured By Wood Item, NEC" },
  { value: "8100", label: "Struck or Injury By, NOC" },
  { value: "0300", label: "Temperature Extremes" },
  { value: "9600", label: "Terrorism" },
  { value: "9999", label: "Unclassified, Insufficient Data" },
  { value: "4800", label: "Vehicle Upset" },
];

/**
 * Canada cause of injury options: US options + ~46 additional entries (148 total).
 * Code 6700 changed from "Standing" to "Sanding, Scraping, Cleaning Operation".
 * Code 8300 changed from "COVID-19" to "Pandemic".
 */
export const SEDGWICK_CANADA_CAUSE_OF_INJURY_OPTIONS: SelectOptionType[] = [
  // US cause of injury with conflicting codes filtered out
  ...SEDGWICK_CAUSE_OF_INJURY_OPTIONS.filter(
    (opt) => opt.value !== "6700" && opt.value !== "8300"
  ),
  // Canada-specific additions (includes Canada versions of 6700 and 8300)
  ...SEDGWICK_CANADA_ADDITIONAL_CAUSE_OF_INJURY,
].sort((a, b) => a.label.localeCompare(b.label));

/**
 * Canadian provinces and territories for province dropdowns.
 */
export const CANADA_PROVINCES: SelectOptionType[] = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon Territory" },
];
